import * as React from 'react';

/**
 * Inline SVG glyphs for the support composer. All stroke icons use currentColor so they tint with
 * the enclosing button's state.
 */

interface IconProps {
	size?: number;
}

const strokeProps = {
	fill: 'none',
	stroke: 'currentColor',
	strokeWidth: 1.6,
	strokeLinecap: 'round' as const,
	strokeLinejoin: 'round' as const,
};

export const UndoIcon: React.FC<IconProps> = ({ size = 15 }) => (
	<svg width={size} height={size} viewBox='0 0 20 20' {...strokeProps} aria-hidden='true'>
		<path d='M8 4 L4 8 L8 12' />
		<path d='M4 8 H12 A4 4 0 0 1 12 16 H9' />
	</svg>
);

export const RedoIcon: React.FC<IconProps> = ({ size = 15 }) => (
	<svg width={size} height={size} viewBox='0 0 20 20' {...strokeProps} aria-hidden='true'>
		<path d='M12 4 L16 8 L12 12' />
		<path d='M16 8 H8 A4 4 0 0 0 8 16 H11' />
	</svg>
);

export const ChevronDownIcon: React.FC<IconProps> = ({ size = 9 }) => (
	<svg width={size} height={size} viewBox='0 0 10 10' {...strokeProps} strokeWidth={1.4} aria-hidden='true'>
		<path d='M2 3.5 L5 6.5 L8 3.5' />
	</svg>
);

export const LinkIcon: React.FC<IconProps> = ({ size = 15 }) => (
	<svg width={size} height={size} viewBox='0 0 20 20' {...strokeProps} aria-hidden='true'>
		<path d='M8 12 L12 8' />
		<path d='M9.2 5.8 L10.8 4.2 A3.1 3.1 0 0 1 15.2 8.6 L13.6 10.2' />
		<path d='M10.8 14.2 L9.2 15.8 A3.1 3.1 0 0 1 4.8 11.4 L6.4 9.8' />
	</svg>
);

export const ImageIcon: React.FC<IconProps> = ({ size = 15 }) => (
	<svg width={size} height={size} viewBox='0 0 20 20' {...strokeProps} aria-hidden='true'>
		<rect x='3' y='4' width='14' height='12' rx='1.5' />
		<circle cx='7.2' cy='8.2' r='1.3' fill='currentColor' stroke='none' />
		<path d='M3.5 14.5 L8 10.5 L11 13 L13.5 10.5 L16.8 13.8' />
	</svg>
);

export const BulletListIcon: React.FC<IconProps> = ({ size = 15 }) => (
	<svg width={size} height={size} viewBox='0 0 20 20' {...strokeProps} aria-hidden='true'>
		<circle cx='4' cy='5' r='1.2' fill='currentColor' stroke='none' />
		<circle cx='4' cy='10' r='1.2' fill='currentColor' stroke='none' />
		<circle cx='4' cy='15' r='1.2' fill='currentColor' stroke='none' />
		<path d='M8.5 5 H17' />
		<path d='M8.5 10 H17' />
		<path d='M8.5 15 H17' />
	</svg>
);

export const OrderedListIcon: React.FC<IconProps> = ({ size = 15 }) => (
	<svg width={size} height={size} viewBox='0 0 20 20' {...strokeProps} aria-hidden='true'>
		<text x='2' y='7' fontSize='6.5' fill='currentColor' stroke='none' fontFamily='sans-serif'>
			1
		</text>
		<text x='2' y='12.5' fontSize='6.5' fill='currentColor' stroke='none' fontFamily='sans-serif'>
			2
		</text>
		<text x='2' y='18' fontSize='6.5' fill='currentColor' stroke='none' fontFamily='sans-serif'>
			3
		</text>
		<path d='M8.5 5 H17' />
		<path d='M8.5 10 H17' />
		<path d='M8.5 15 H17' />
	</svg>
);

export const AlignIcon: React.FC<IconProps> = ({ size = 15 }) => (
	<svg width={size} height={size} viewBox='0 0 20 20' {...strokeProps} aria-hidden='true'>
		<path d='M3 5 H17' />
		<path d='M3 10 H12' />
		<path d='M3 15 H15' />
	</svg>
);

export const ExpandIcon: React.FC<IconProps> = ({ size = 15 }) => (
	<svg width={size} height={size} viewBox='0 0 20 20' {...strokeProps} strokeWidth={1.5} aria-hidden='true'>
		<path d='M12 3 H17 V8' />
		<path d='M17 3 L11.5 8.5' />
		<path d='M8 17 H3 V12' />
		<path d='M3 17 L8.5 11.5' />
	</svg>
);

export const CollapseIcon: React.FC<IconProps> = ({ size = 15 }) => (
	<svg width={size} height={size} viewBox='0 0 20 20' {...strokeProps} strokeWidth={1.5} aria-hidden='true'>
		<path d='M16 9 H11 V4' />
		<path d='M11 9 L16.5 3.5' />
		<path d='M4 11 H9 V16' />
		<path d='M9 11 L3.5 16.5' />
	</svg>
);

export const CloseIcon: React.FC<IconProps> = ({ size = 14 }) => (
	<svg width={size} height={size} viewBox='0 0 20 20' {...strokeProps} strokeWidth={1.5} aria-hidden='true'>
		<path d='M5 5 L15 15' />
		<path d='M15 5 L5 15' />
	</svg>
);

export const PaperclipIcon: React.FC<IconProps> = ({ size = 15 }) => (
	<svg width={size} height={size} viewBox='0 0 20 20' {...strokeProps} aria-hidden='true'>
		<path d='M14.8 8.7 L9.4 14.1 A2.6 2.6 0 0 1 5.7 10.4 L11.5 4.6 A3.6 3.6 0 0 1 16.6 9.7 L10.8 15.5' />
	</svg>
);

export const BoltIcon: React.FC<IconProps> = ({ size = 15 }) => (
	<svg width={size} height={size} viewBox='0 0 20 20' aria-hidden='true'>
		<path d='M11 2 L4.5 11 H9 L8 18 L15.5 9 H11 Z' fill='currentColor' />
	</svg>
);

export const FileIcon: React.FC<IconProps> = ({ size = 13 }) => (
	<svg width={size} height={size} viewBox='0 0 20 20' {...strokeProps} strokeWidth={1.5} aria-hidden='true'>
		<path d='M6 3 H11.5 L15 6.5 V17 H6 Z' />
		<path d='M11.5 3 V6.5 H15' />
	</svg>
);

interface SparkleIconProps extends IconProps {
	/** Gradient stop colors; defaults to the Levitate blue → potion pink AI gradient. */
	from?: string;
	to?: string;
}

export const SparkleIcon: React.FC<SparkleIconProps> = ({ size = 15, from = '#0066ff', to = '#ca2c99' }) => {
	const gradientId = React.useId();
	return (
		<svg width={size} height={size} viewBox='0 0 20 20' aria-hidden='true'>
			<defs>
				<linearGradient id={gradientId} x1='0' y1='0' x2='1' y2='0'>
					<stop offset='0' stopColor={from} />
					<stop offset='1' stopColor={to} />
				</linearGradient>
			</defs>
			<path d='M10 2 L11.7 8.3 L18 10 L11.7 11.7 L10 18 L8.3 11.7 L2 10 L8.3 8.3 Z' fill={`url(#${gradientId})`} />
		</svg>
	);
};
